import { computed, type Ref } from "vue";

const normalizeReviewRating = (value: unknown) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.min(5, parsed));
};

export function useProductReviews(product: Ref<Record<string, any> | null>) {
  const productReviews = computed(() => {
    if (!Array.isArray(product.value?.reviews)) {
      return [];
    }

    return product.value.reviews.filter((review: any) => {
      if (!review || typeof review !== "object") {
        return false;
      }

      const hasAuthor = String(review.author ?? "").trim().length > 0;
      const hasContent = String(review.content ?? "").trim().length > 0;
      const hasRating = normalizeReviewRating(review.rating) > 0;

      return hasAuthor || hasContent || hasRating;
    });
  });

  const hasProductReviews = computed(() => productReviews.value.length > 0);

  const productRating = computed(() => {
    const rawRating = normalizeReviewRating(product.value?.rating);

    if (rawRating > 0) {
      return rawRating;
    }

    const ratings = productReviews.value
      .map((review: any) => normalizeReviewRating(review?.rating))
      .filter((rating) => rating > 0);

    if (!ratings.length) {
      return 0;
    }

    const total = ratings.reduce((sum, rating) => sum + rating, 0);

    return total / ratings.length;
  });

  return {
    productReviews,
    hasProductReviews,
    productRating,
  };
}
